import { createModel } from '@rematch/core'
import type { RootModel } from '.'
import { graphQLAdminUser,graphQLAdminUsers } from '../services/graphQLRequest'

interface AdminUser {
  id: string
  email: string
  created: string
  lastLogin?: string
  [key: string]: any
}

interface AdminUsersState {
  users: AdminUser[]
  total: number
  hasMore: boolean
  loading: boolean
  page: number
  pageSize: number
  searchValue: string
  searchType: 'all' | 'email' | 'userId'
  detailCache: { [userId: string]: AdminUser }
}

const initialState: AdminUsersState = {
  users: [],
  total: 0,
  hasMore: false,
  loading: false,
  page: 1,
  pageSize: 50,
  searchValue: '',
  searchType: 'email',
  detailCache: {}
}

export const adminUsers = createModel<RootModel>()({
  name: 'adminUsers',
  state: initialState,
  reducers: {
    setUsers: (state, payload: { users: AdminUser[]; total: number; hasMore: boolean }) => ({
      ...state,
      users: payload.users,
      total: payload.total,
      hasMore: payload.hasMore,
      loading: false
    }),
    appendUsers: (state, payload: { users: AdminUser[]; total: number; hasMore: boolean }) => ({
      ...state,
      users: [...state.users, ...payload.users],
      total: payload.total,
      hasMore: payload.hasMore,
      loading: false
    }),
    setLoading: (state, loading: boolean) => ({
      ...state,
      loading
    }),
    setPage: (state, page: number) => ({
      ...state,
      page
    }),
    setSearch: (state, payload: { searchValue: string; searchType: 'all' | 'email' | 'userId' }) => ({
      ...state,
      searchValue: payload.searchValue,
      searchType: payload.searchType,
      page: 1 // Reset to first page on new search
    }),
    cacheUserDetail: (state, payload: { userId: string; user: AdminUser }) => {
      // Merge so sparse list data (e.g. from a list refresh) doesn't clobber
      // detail-only fields already cached (lastLogin, online/offline, org, ...)
      const existing = state.detailCache[payload.userId]
      const user = existing
        ? {
            ...existing,
            ...payload.user,
            info: {
              ...existing.info,
              ...payload.user.info,
              devices: {
                ...existing.info?.devices,
                ...payload.user.info?.devices
              }
            }
          }
        : payload.user
      return {
        ...state,
        detailCache: {
          ...state.detailCache,
          [payload.userId]: user
        }
      }
    },
    invalidateUserDetail: (state, userId: string) => {
      const newCache = { ...state.detailCache }
      delete newCache[userId]
      return {
        ...state,
        detailCache: newCache
      }
    },
    reset: () => initialState
  },
  effects: (dispatch) => ({
    async fetch(_: void, rootState) {
      const state = rootState.adminUsers
      dispatch.adminUsers.setLoading(true)

      // Build filters based on search type
      const filters: { search?: string; email?: string; accountId?: string } = {}
      const trimmedValue = state.searchValue.trim()

      if (trimmedValue) {
        switch (state.searchType) {
          case 'email':
            filters.email = trimmedValue
            break
          case 'userId':
            filters.accountId = trimmedValue
            break
          case 'all':
          default:
            filters.search = trimmedValue
            break
        }
      }

      const result = await graphQLAdminUsers(
        { from: (state.page - 1) * state.pageSize, size: state.pageSize },
        Object.keys(filters).length > 0 ? filters : undefined,
        'email'
      )

      if (result !== 'ERROR' && result?.data?.data?.admin?.users) {
        const data = result.data.data.admin.users
        const users = data.items || []
        dispatch.adminUsers.setUsers({
          users,
          total: data.total || 0,
          hasMore: !!data.hasMore
        })

        users.forEach((user: AdminUser) => {
          dispatch.adminUsers.cacheUserDetail({ userId: user.id, user })
        })
      } else {
        dispatch.adminUsers.setLoading(false)
      }
    },
    async fetchMore(_: void, rootState) {
      const state = rootState.adminUsers
      if (!state.hasMore || state.loading) return

      dispatch.adminUsers.setLoading(true)

      const from = state.users.length
      const filters: { search?: string; email?: string; accountId?: string } = {}
      const trimmedValue = state.searchValue.trim()

      if (trimmedValue) {
        switch (state.searchType) {
          case 'email':
            filters.email = trimmedValue
            break
          case 'userId':
            filters.accountId = trimmedValue
            break
          case 'all':
          default:
            filters.search = trimmedValue
            break
        }
      }

      const result = await graphQLAdminUsers(
        { from, size: state.pageSize },
        Object.keys(filters).length > 0 ? filters : undefined,
        'email'
      )

      if (result !== 'ERROR' && result?.data?.data?.admin?.users) {
        const data = result.data.data.admin.users
        const users = data.items || []
        dispatch.adminUsers.appendUsers({
          users,
          total: data.total || 0,
          hasMore: !!data.hasMore
        })

        users.forEach((user: AdminUser) => {
          dispatch.adminUsers.cacheUserDetail({ userId: user.id, user })
        })
      } else {
        dispatch.adminUsers.setLoading(false)
      }
    },
    async fetchIfEmpty(_: void, rootState) {
      if (rootState.adminUsers.users.length === 0) {
        await dispatch.adminUsers.fetch(undefined)
      }
    },
    async fetchUserDetail(payload: string | { userId: string; force?: boolean }, rootState) {
      const { userId, force } = typeof payload === 'string' ? { userId: payload, force: false } : payload

      // Use cache unless a forced refresh is requested. On force we keep the
      // existing cache entry in place and overwrite it once fresh data arrives
      // (via cacheUserDetail's merge) so the UI never shows an empty state.
      const cached = rootState.adminUsers.detailCache[userId]
      if (cached && !force) {
        return cached
      }

      // Fetch from API
      const result = await graphQLAdminUser(userId)
      if (result !== 'ERROR' && result?.data?.data?.admin?.users?.items?.[0]) {
        const user = result.data.data.admin.users.items[0]
        dispatch.adminUsers.cacheUserDetail({ userId, user })
        return user
      }
      return cached || null
    }
  })
})
